import React from "react";
import {Box, Button} from "@mui/material";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import {Link as RouterLink} from "react-router-dom";
import {useAuthManager} from "./auth";
import {Prose} from "../wiki/prose";
import {useWikiPage} from "../wiki/use_wiki_page";
import {wikiAdminEditPath} from "../wiki/sections";
import {btnSx, EmptyState, SkeletonRows} from "../kit";

export interface WikiPageRenderProps {
	code?: string;
	section?: string;
	content?: string;
}

export const useIsAdmin = () => {
	const authManager = useAuthManager();
	return authManager.getAuthorities().some(r => r.type && r.type.indexOf("ADMIN") >= 0);
};

/** Una página de la wiki dentro de otra pantalla: por código y sección, o con el contenido ya dado. */
const WikiPageRender = ({code, section, content}: WikiPageRenderProps) => {
	if (code && !section) {
		throw new Error("Section is mandatory when code is set");
	}
	if (content !== undefined) {
		return <Prose content={content}/>;
	}
	return <LoadedWikiPage code={code!} section={section!}/>;
};

const LoadedWikiPage = ({code, section}: { code: string, section: string }) => {
	const isAdmin = useIsAdmin();
	const {page, loading, notFound} = useWikiPage(code, section);

	if (loading) return <SkeletonRows rows={5} height={20}/>;
	if (notFound || !page) {
		return <EmptyState icon={<ArticleOutlinedIcon/>} title="No encontramos esta página"
						   description="Puede que se haya retirado o que aún no esté publicada."/>;
	}
	return (
		<Box>
			{isAdmin && page.id && (
				<Box sx={{display: "flex", justifyContent: "flex-end", mb: 1}}>
					<Button size="small" component={RouterLink} to={wikiAdminEditPath(section, page.id)}
							startIcon={<EditRoundedIcon/>} sx={btnSx}>Editar</Button>
				</Box>
			)}
			{page.content?.trim()
				? <Prose content={page.content}/>
				: <EmptyState icon={<ArticleOutlinedIcon/>} title="Esta página aún no tiene contenido"/>}
		</Box>
	);
};

export default WikiPageRender;
